"use client";

import * as React from "react";
import { Select as SelectPrimitive } from "@base-ui/react/select";

import { cn } from "@/lib/utils";
import { IconCheck, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { observer } from "mobx-react";
import { useReportedChange } from "@/components/ui/unsaved-changes";

/** Wraps base-ui's Root only to tell an enclosing dialog the person picked something
 *  (NG-2717). `onValueChange` fires on user picks and typeahead, never for a controlled
 *  `value` change, so a pre-filled Select does not count as an edit. */
// `observer` drops the generic signature, so the cast restores it and keeps `value` /
// `onValueChange` typed at every call site.
const Select = observer(function Select<Value, Multiple extends boolean | undefined = false>({
  onValueChange,
  ...props
}: SelectPrimitive.Root.Props<Value, Multiple>) {
  return <SelectPrimitive.Root {...props} onValueChange={useReportedChange(onValueChange)} />;
}) as <Value, Multiple extends boolean | undefined = false>(
  props: SelectPrimitive.Root.Props<Value, Multiple>,
) => React.JSX.Element;

const SelectGroup = observer(function SelectGroup({
  className,
  ...props
}: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn("scroll-my-1 p-1", className)}
      {...props}
    />
  );
});

/**
 * The trigger's closed-state value.
 *
 * It is a **block** box, not a flex one, and that is load-bearing: `truncate`
 * ellipsises by way of `text-overflow`, which reaches text only in a block
 * container. Inside a flex box the same text becomes an anonymous flex item that
 * `text-overflow` cannot address, so it clips mid-character instead (NG-869).
 *
 * Every call site passes a single child — a string, a pill, or a span that does
 * its own inline-flex — so nothing here needs a flex row of its own. Keep it that
 * way: a sibling *pair* added under this slot would sit with no gap between them.
 */
const SelectValue = observer(function SelectValue({
  className,
  ...props
}: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn("block flex-1 truncate text-left", className)}
      {...props}
    />
  );
});

/**
 * The closed control.
 *
 * `w-fit max-w-full` is the width contract: it still sizes to its own content, so
 * a compact toolbar picker stays compact, but it can never grow past its
 * container. Without the cap, `w-fit` plus `whitespace-nowrap` resolves to the
 * full width of the selected label, which is how one long workspace-authored
 * option dragged an entire settings panel into a horizontal scroll (NG-869).
 * Callers needing the trigger to *fill* its row still say so (`w-full`, or
 * `flex-1 min-w-0` as a flex item) — the cap is a floor under them, not a
 * replacement.
 */
const SelectTrigger = observer(function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: "sm" | "default";
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "flex w-fit max-w-full items-center justify-between gap-1.5 rounded-md border border-border bg-background py-1.5 pr-3 pl-2.5 text-xs/relaxed whitespace-nowrap transition-colors outline-none hover:bg-muted focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20 data-placeholder:text-muted-foreground data-[size=default]:h-7 data-[size=sm]:h-6 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={
          <IconChevronDown
            stroke={2}
            className="pointer-events-none size-3.5 text-muted-foreground"
          />
        }
      />
    </SelectPrimitive.Trigger>
  );
});

const SelectContent = observer(function SelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  alignItemWithTrigger = true,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger"
  >) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            // Width: grow to fit the options, floored at the trigger's width and
            // capped at what the viewport allows. It was `w-(--anchor-width)`,
            // i.e. exactly the trigger — and with `overflow-x-hidden` that HARD
            // CLIPPED any option longer than the trigger, mid-word, with no
            // ellipsis and no way to read it. That is unreadable for option
            // labels the app doesn't control (workspace-authored review
            // outcomes, custom field choices) — NG-920. Keep the floor so a
            // narrow trigger still gets a menu at least its own width.
            "relative isolate z-50 max-h-(--available-height) w-fit max-w-(--available-width) min-w-[max(var(--anchor-width),8rem)] origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 transition duration-(--motion-duration-base) ease-motion-out data-ending-style:opacity-0 data-starting-style:opacity-0 data-[align-trigger=false]:data-ending-style:scale-95 data-[align-trigger=false]:data-starting-style:scale-95 data-[align-trigger=false]:data-[side=bottom]:data-ending-style:-translate-y-2 data-[align-trigger=false]:data-[side=bottom]:data-starting-style:-translate-y-2 data-[align-trigger=false]:data-[side=inline-end]:data-ending-style:-translate-x-2 data-[align-trigger=false]:data-[side=inline-end]:data-starting-style:-translate-x-2 data-[align-trigger=false]:data-[side=inline-start]:data-ending-style:translate-x-2 data-[align-trigger=false]:data-[side=inline-start]:data-starting-style:translate-x-2 data-[align-trigger=false]:data-[side=left]:data-ending-style:translate-x-2 data-[align-trigger=false]:data-[side=left]:data-starting-style:translate-x-2 data-[align-trigger=false]:data-[side=right]:data-ending-style:-translate-x-2 data-[align-trigger=false]:data-[side=right]:data-starting-style:-translate-x-2 data-[align-trigger=false]:data-[side=top]:data-ending-style:translate-y-2 data-[align-trigger=false]:data-[side=top]:data-starting-style:translate-y-2",
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
});

const SelectLabel = observer(function SelectLabel({
  className,
  ...props
}: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn("px-2 py-1.5 text-xs text-muted-foreground", className)}
      {...props}
    />
  );
});

/**
 * One option row.
 *
 * `pr-8` is a reserved gutter for the selected-item check, not decoration. The
 * indicator is absolutely positioned at `right-2`, so it is out of flow and the
 * popup's `w-fit` cannot see it, while `ItemText` is `shrink-0 whitespace-nowrap`
 * and sizes the popup to its own max-content. Without the gutter the two meet:
 * a long option label runs underneath the check with no space between them, which
 * is what NG-920's wider popup exposed (a label the app doesn't control — a
 * workspace-authored choice — is as long as someone typed).
 *
 * Reserved on every row rather than only the selected one so the checks line up
 * on a common right edge instead of shifting with each label's length.
 */
const SelectItem = observer(function SelectItem({
  className,
  children,
  ...props
}: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex min-h-7 w-full cursor-default items-center gap-2 rounded-md py-1 pr-8 pl-2 text-xs/relaxed outline-hidden select-none focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute right-2 flex items-center justify-center" />
        }
      >
        <IconCheck stroke={2} className="pointer-events-none" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});

const SelectSeparator = observer(function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("pointer-events-none -mx-1 my-1 h-px bg-border/50", className)}
      {...props}
    />
  );
});

const SelectScrollUpButton = observer(function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "top-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-3.5",
        className,
      )}
      {...props}
    >
      <IconChevronUp stroke={2} />
    </SelectPrimitive.ScrollUpArrow>
  );
});

const SelectScrollDownButton = observer(function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-3.5",
        className,
      )}
      {...props}
    >
      <IconChevronDown stroke={2} />
    </SelectPrimitive.ScrollDownArrow>
  );
});

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
